import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { useTabActive } from "../../lib/tabActive";
import { Icon } from "./Icon";
import { AY_ADLARI, GUN_KISA, addDays, addMonths, fmtTR, fromISO, pzIndex, toISO, todayISO } from "../../lib/trDate";
import "./DateField.css";

/**
 * TARİH ALANI — `<input type="date">` yerine geçen takvimli seçici.
 *
 * ── NEDEN ─────────────────────────────────────────────────────────────────────
 * Yerel `type="date"` denetimi tarayıcıya göre değişiyor: yazı tipi bizim değil, takvim
 * ikonu tarayıcının (küçük, hizasız), gg.aa.yyyy yer tutucusu düzenlenemiyor ve alan
 * "tıklanabilir" hissi vermiyordu. Sözleşme AYNI kaldı — `value`/`onChange` yine
 * "YYYY-MM-DD" metniyle çalışır — böylece bu bileşen mevcut `type="date"` kullanımlarının
 * yerine BİREBİR geçebilir.
 *
 * ── DESEN ─────────────────────────────────────────────────────────────────────
 * Açılır katman mekaniği `SearchSelect` ile AYNI (kod tabanındaki tek açılır-panel deseni):
 *  · panel `document.body`ye portal → tablo/kart `overflow:hidden` kırpmaz,
 *  · yer yoksa YUKARI açılır,
 *  · `useTabActive()` kapısı — sekme kabuğu TÜM sekmeleri monte tuttuğu için, pasif
 *    sekmede açık kalmış bir panel aktif sayfanın üstünde asılı kalırdı,
 *  · ESC yayılımı KESİLİR — yoksa paneli kapatan ESC üstteki çekmeceyi de kapatır.
 *
 * Klavye: ←→ gün · ↑↓ hafta · PgUp/PgDn ay · Home/End hafta başı/sonu · ↵ seç · esc kapat.
 */

interface Props {
  /** "YYYY-MM-DD" ya da boş metin. */
  value: string;
  onChange: (value: string) => void;
  id?: string;
  /** En erken seçilebilir gün, "YYYY-MM-DD". */
  min?: string;
  /** En geç seçilebilir gün, "YYYY-MM-DD". */
  max?: string;
  disabled?: boolean;
  error?: boolean;
  clearable?: boolean;
  placeholder?: string;
  /** Panel altındaki "Bugün" kısayolu (min/max dışındaysa kendiliğinden pasifleşir). */
  showToday?: boolean;
  "aria-label"?: string;
}

export function DateField({
  value, onChange, id, min, max, disabled, error,
  clearable = true, placeholder = "gg.aa.yyyy", showToday = true,
  "aria-label": ariaLabel,
}: Props) {
  const [open, setOpen] = useState(false);
  const tabActive = useTabActive();
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top?: number; bottom?: number; left: number }>({ top: 0, left: 0 });

  const bugun = todayISO();
  const secili = useMemo(() => fromISO(value), [value]);
  /** Panelde görünen ay. Değer yoksa bugünün ayı — boş bir forma 1970 açılmasın. */
  const [cursor, setCursor] = useState<Date>(() => secili ?? fromISO(bugun)!);
  /** Klavye odağındaki gün (seçim DEĞİL — ↵ ile seçilir). */
  const [odak, setOdak] = useState<Date>(() => secili ?? fromISO(bugun)!);

  const minD = useMemo(() => (min ? fromISO(min) : null), [min]);
  const maxD = useMemo(() => (max ? fromISO(max) : null), [max]);
  const disabledGun = (d: Date) => (minD != null && d < minD) || (maxD != null && d > maxD);

  // Açılışta seçili aya/güne konumlan (kapalıyken dış değer değişirse de tazelenir).
  useEffect(() => {
    if (!open) return;
    const b = secili ?? fromISO(bugun)!;
    setCursor(b);
    setOdak(b);
  }, [open, value]); // eslint-disable-line react-hooks/exhaustive-deps

  // Dış tık → kapat (panel portal'landığı için panelRef de kontrol edilir).
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Panel konumu (fixed) — trigger'a göre; scroll/resize'da güncelle. Altta yer yoksa yukarı aç.
  useEffect(() => {
    if (!open) return;
    /**
     * ⚠ PANEL ÖLÇÜLÜR, SABİTLE TAHMİN EDİLMEZ — VE EKRANA KISTIRILIR.
     * Eskiden yükseklik `330` sabitiydi (masaüstü ölçüsü). Mobil CSS (44px gün hücreleri, büyük
     * gezinme) paneli ~417px yapıyor; yön kararı yanlış veriliyor, panel aşağı açılıp altı
     * KIRPILIYOR ya da yukarı açılıp başlığı (ay değiştirme) ekranın üstünden taşıyordu. Yatayda
     * `left = r.left` + sabit genişlik: 430px'lik telefonlarda sağ sütundaki tarih alanının
     * takvimi ~111px ekran dışına düşüyor, "Sonraki ay" düğmesine ulaşılamıyordu.
     * Panel `fixed` olduğu için taşan kısım kaydırılarak da görülemez — sığmak ZORUNDA.
     * Efekt işlediğinde panel DOM'dadır (open ile aynı çizimde basılır) → ölçmek güvenli.
     */
    const update = () => {
      const r = rootRef.current?.getBoundingClientRect();
      if (!r) return;
      const vh = window.innerHeight;
      const vw = document.documentElement.clientWidth;
      const H = panelRef.current?.offsetHeight || 330; // ilk çizimde ölçü yoksa masaüstü tahmini
      const W = panelRef.current?.offsetWidth || 268;
      const left = Math.min(Math.max(8, r.left), Math.max(8, vw - W - 8));
      const below = vh - r.bottom - 8;
      const above = r.top - 8;
      if (below >= H) setCoords({ top: r.bottom + 4, left });
      else if (above >= H) setCoords({ bottom: vh - r.top + 4, left });
      // İki yöne de sığmıyor (telefonun ortasındaki alan): tetikleyiciyi örtme pahasına
      // TAMAMEN görünür konuma yasla. Kırpılmış takvim, örtülen tetikleyiciden kötüdür.
      else setCoords({ top: Math.max(8, vh - H - 8), left });
    };
    update();
    // İlk ölçüm çizimden önce 0 dönebilir (animasyon/yazı tipi) → bir kare sonra yeniden ölç.
    const ilkKare = window.setTimeout(update, 0);
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.clearTimeout(ilkKare);
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  // Odak hücresini gerçekten odakla — klavye gezinmesi ekran okuyucuya da duyurulsun.
  useEffect(() => {
    if (!open) return;
    gridRef.current?.querySelector<HTMLElement>('[data-odak="1"]')?.focus();
  }, [open, odak]);

  /** Görünen ayın ızgarası: Pazartesi-ilk, 6 satır sabit (panel zıplamasın). */
  const gunler = useMemo(() => {
    const ilk = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const bas = addDays(ilk, -pzIndex(ilk));
    return Array.from({ length: 42 }, (_, i) => addDays(bas, i));
  }, [cursor]);

  function sec(d: Date) {
    if (disabledGun(d)) return;
    onChange(toISO(d));
    setOpen(false);
  }

  function gez(d: Date) {
    setOdak(d);
    if (d.getMonth() !== cursor.getMonth() || d.getFullYear() !== cursor.getFullYear()) setCursor(d);
  }

  function onKey(e: KeyboardEvent) {
    const k = e.key;
    if (k === "Escape") {
      // Yayılımı KES: aksi halde paneli kapatan ESC üstteki çekmeceye ulaşıp onu da kapatır.
      e.stopPropagation();
      setOpen(false);
      return;
    }
    if (k === "Enter" || k === " ") { e.preventDefault(); sec(odak); return; }
    let hedef: Date | null = null;
    if (k === "ArrowLeft") hedef = addDays(odak, -1);
    else if (k === "ArrowRight") hedef = addDays(odak, 1);
    else if (k === "ArrowUp") hedef = addDays(odak, -7);
    else if (k === "ArrowDown") hedef = addDays(odak, 7);
    else if (k === "PageUp") hedef = addMonths(odak, -1);
    else if (k === "PageDown") hedef = addMonths(odak, 1);
    else if (k === "Home") hedef = addDays(odak, -pzIndex(odak));
    else if (k === "End") hedef = addDays(odak, 6 - pzIndex(odak));
    if (hedef) { e.preventDefault(); gez(hedef); }
  }

  const gosterim = fmtTR(value);
  const bugunD = fromISO(bugun)!;
  const bugunSecilemez = disabledGun(bugunD);

  return (
    <div className="df-root" ref={rootRef}>
      <button
        type="button"
        id={id}
        className={`df-trigger${open ? " open" : ""}${error ? " error" : ""}`}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <span className="df-ic"><Icon name="calendar" size={14} /></span>
        <span className={`df-val${gosterim ? "" : " placeholder"}`}>{gosterim || placeholder}</span>
        {clearable && gosterim && !disabled ? (
          <span
            role="button"
            tabIndex={-1}
            className="df-clear"
            aria-label="Tarihi temizle"
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onChange(""); }}
          >
            <Icon name="x" size={12} />
          </span>
        ) : null}
      </button>

      {/* Panel PASİF SEKMEDE basılmaz — portal `document.body`ye gider, sekme panelinin
          gizlenmesi ona ulaşmaz (SearchSelect.tsx:209 ile aynı gerekçe). */}
      {open && tabActive ? createPortal(
        <div
          className="df-panel"
          ref={panelRef}
          role="dialog"
          aria-label="Tarih seç"
          style={{ top: coords.top, bottom: coords.bottom, left: coords.left }}
          onKeyDown={onKey}
        >
          <div className="df-head">
            <button type="button" className="df-nav" onClick={() => setCursor(addMonths(cursor, -1))} aria-label="Önceki ay">
              <Icon name="chevLeft" size={15} />
            </button>
            <span className="df-title" aria-live="polite">
              {AY_ADLARI[cursor.getMonth()]} <b>{cursor.getFullYear()}</b>
            </span>
            <button type="button" className="df-nav" onClick={() => setCursor(addMonths(cursor, 1))} aria-label="Sonraki ay">
              <Icon name="chevRight" size={15} />
            </button>
          </div>

          <div className="df-dow" aria-hidden="true">
            {GUN_KISA.map((g) => <span key={g}>{g}</span>)}
          </div>

          <div className="df-grid" ref={gridRef} role="grid">
            {gunler.map((d) => {
              const iso = toISO(d);
              const disaAy = d.getMonth() !== cursor.getMonth();
              const isSecili = iso === value;
              const isBugun = iso === bugun;
              const kapali = disabledGun(d);
              const odakli = toISO(odak) === iso;
              return (
                <button
                  type="button"
                  key={iso}
                  data-odak={odakli ? "1" : undefined}
                  tabIndex={odakli ? 0 : -1}
                  className={`df-day${disaAy ? " out" : ""}${isSecili ? " sel" : ""}${isBugun ? " today" : ""}`}
                  disabled={kapali}
                  aria-current={isBugun ? "date" : undefined}
                  aria-selected={isSecili}
                  role="gridcell"
                  onClick={() => sec(d)}
                  onFocus={() => setOdak(d)}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>

          <div className="df-foot">
            {showToday ? (
              <button type="button" className="df-today" onClick={() => sec(bugunD)} disabled={bugunSecilemez}>
                Bugün · {fmtTR(bugun)}
              </button>
            ) : <span />}
            <span className="df-keys"><kbd>←→</kbd> <kbd>↵</kbd> <kbd>esc</kbd></span>
          </div>
        </div>,
        document.body,
      ) : null}
    </div>
  );
}
